import React, { useState, useEffect } from "react";
import styles from "./index.module.css";
import {
  Button,
  Upload,
  Form,
  DatePicker,
  Alert,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { validationRules } from "../../common/Utils";
import {
  getFields,
  saveStep,
  updateStep,
  uploadFile,
} from "../../../store/policy";
import {
  getDocumentFileId,
  getSecureDocumentUrl,
} from "../../../utils/document";
import moment from "moment";
import {
  PenTool,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Upload as UploadIcon,
  ArrowLeft,
  ArrowRight,
  Loader,
  Trash2,
} from "lucide-react";

const signatureTooltip = [
  "Click 'Upload Signature' and select a clear image (JPG, PNG, or GIF).",
  "Ensure your signature is well-lit and readable.",
  "Your signature will be saved automatically after upload.",
];

function Index({ current, setCurrent }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [showLess, setShowLess] = useState(true);
  const [fileList, setFileList] = useState();
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState(null);
  const [signatureUrlLoading, setSignatureUrlLoading] = useState(false);
  const [sortedFields, setSortedFields] = useState([]);
  const existingValues = sessionStorage.getItem("values");
  let initial = true;

  const nextStep = () => {
    let values = form.getFieldsValue();
    const formData = new Object();
    Object.keys(values).forEach((key) => {
      if (key === "signature_date") {
        formData[key] = moment(values["signature_date"]).format(
          "MM/DD/YYYY"
        );
      } else {
        formData[key] = values[key];
      }
    });

    // Explicitly add file fields managed via state (not registered as Form.Item)
    if (fileList) {
      const fileField = fields?.find((f) => f.type === "file");
      if (fileField) {
        formData[fileField.field_name] = fileList;
      }
    }

    const mappedArray = Object.keys(formData)?.map((key, i) => {
      const field = fields?.find((field) => field.field_name === key);
      if (field) {
        return {
          field_id: field.id,
          key: field.field_name,
          value: formData[key],
        };
      }
    });
    if (
      !form.isFieldsTouched() &&
      existingValues &&
      JSON.parse(existingValues).find(
        (step) =>
          step.step_id === steps.find((s) => s?.step_number === current)?.id
      )
    ) {
      setCurrent(5);
    } else if (
      existingValues &&
      JSON.parse(existingValues)?.find(
        (step) =>
          step.step_id ===
          steps?.find((s) => s?.step_number === current)?.id
      )
    ) {
      dispatch(
        updateStep({
          userId: JSON.parse(sessionStorage.getItem("user"))?.userId,
          stepId: steps?.find((s) => s?.step_number === current)?.id,
          parameters: mappedArray,
        })
      );
      localStorage.removeItem("lastStep");
    } else {
      dispatch(
        saveStep({
          source: steps?.find((s) => s?.step_number === current)
            ?.step_source,
          user_id: JSON.parse(sessionStorage.getItem("user"))?.userId,
          step_id: steps?.find((s) => s?.step_number === current)?.id,
          parameters: mappedArray,
        })
      );
    }
    setCurrent(5)
  };

  const { steps, fields, fileUploading } = useSelector(
    (state) => state.policySlice
  );

  useEffect(() => {
    if (
      steps &&
      steps?.find((s) => s?.step_number === current) &&
      steps?.find((s) => s?.step_number === current).form_id &&
      steps?.find((s) => s?.step_number === current).id
    ) {
      dispatch(
        getFields({
          formId: steps?.find((s) => s?.step_number === current).form_id,
          stepId: steps?.find((s) => s?.step_number === current).id,
        })
      ).then((res) => {
        const localFields = res?.payload &&
          Array.isArray(res?.payload) && [...res?.payload];
        setSortedFields(
          localFields?.sort((a, b) => a?.field_sequence - b?.field_sequence)
        );
      });
    }
  }, [steps]);

  useEffect(() => {
    if (
      existingValues &&
      JSON.parse(existingValues)?.find(
        (step) =>
          step.step_id === steps?.find((s) => s?.step_number === current)?.id
      ) &&
      initial
    ) {
      initial = false;
      const values = JSON.parse(existingValues)?.find(
        (step) =>
          step.step_id === steps?.find((s) => s?.step_number === current)?.id
      ).parameters;

      const obj = new Object();
      const fileField = sortedFields?.find((f) => f.type === "file");
      for (let key of values) {
        if (key.key === "signature_date" && key.value) {
          obj[key.key] = moment(key.value, "MM/DD/YYYY");
        } else if (fileField && key.key === fileField.field_name && key.value) {
          obj[key.key] = key.value;
          setFileList(key.value);
        } else if (key.key === "signature_upload" && key.value) {
          obj[key.key] = key.value;
          setFileList(key.value);
        } else {
          obj[key.key] = key.value;
        }
      }
      form.setFieldsValue(obj);
    }
  }, [steps]);

  useEffect(() => {
    const fileId = fileList && getDocumentFileId(fileList);
    if (fileId) {
      setSignatureUrlLoading(true);
      getSecureDocumentUrl(fileId).then((url) => {
        setSignaturePreviewUrl(url);
        setSignatureUrlLoading(false);
      });
    } else {
      setSignaturePreviewUrl(null);
      setSignatureUrlLoading(false);
    }
  }, [fileList]);

  const handleChange = async ({ file }) => {
    console.log(file);
    if (file.status === "removed") {
      setFileList();
      form.resetFields();
      return;
    } else {
      const formData = new FormData();
      formData.append("file", file);
      dispatch(uploadFile(formData))
        .then((res) => {
          form.setFieldsValue({
            signature_date: moment(),
            signature_upload: res?.payload?.result,
          });
          setFileList({
            ...res?.payload?.result,
            name: res?.payload?.result?.file_name,
          });
        })
        .catch((err) => console.log(err));
    }
  };

  return (
    <div className={styles.stepWrapper}>
      <Form className={styles.formContainer} layout="vertical" form={form}>
        {sortedFields &&
          sortedFields.length > 0 &&
          sortedFields?.map((field, index) => {
            if (field?.type === "information") {
              const declarations = field?.information?.split("<br>\n") || [];
              const visibleDeclarations = showLess ? declarations.slice(0, 2) : declarations;
              return (
                <div className={styles.card} key={index}>
                  <div className={styles.cardHeader}>
                    <div className={styles.cardIcon}>
                      <CheckCircle size={22} />
                    </div>
                    <div>
                      <h3 className={styles.cardTitle}>
                        {field?.heading || "Declarations"}
                      </h3>
                      {field?.main_heading && (
                        <span className={styles.cardDesc}>{field.main_heading}</span>
                      )}
                    </div>
                  </div>
                  <div className={styles.cardBody}>
                    <div className={styles.declarationsList}>
                      {visibleDeclarations.map((declaration, idx) => (
                        <div key={idx} className={styles.declarationItem}>
                          <CheckCircle size={16} color="#028D86" />
                          <span>{declaration}</span>
                        </div>
                      ))}
                    </div>
                    {declarations.length > 2 && (
                      <button
                        type="button"
                        className={styles.showMoreBtn}
                        onClick={() => setShowLess(!showLess)}
                      >
                        {showLess ? (
                          <>Show More <ChevronDown size={14} /></>
                        ) : (
                          <>Show Less <ChevronUp size={14} /></>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            } else if (field?.type === "text") {
              return (
                <button
                  key={index}
                  type="button"
                  className={styles.termsLink}
                  onClick={() => window.open('https://www.edencaremedical.com/legal/terms-of-service', '_blank')}
                >
                  <ExternalLink size={16} />
                  {field?.heading}
                </button>
              );
            } else if (field?.type === "file") {
              return (
                <div className={styles.card} key={index} style={{ marginTop: 20 }}>
                  <div className={styles.cardHeader}>
                    <div className={styles.cardIcon}>
                      <PenTool size={22} />
                    </div>
                    <div>
                      <h3 className={styles.cardTitle}>
                        {field?.heading || "Signature"}
                      </h3>
                      {field?.main_heading && (
                        <span className={styles.cardDesc}>{field.main_heading}</span>
                      )}
                    </div>
                  </div>
                  <div className={styles.cardBody}>
                    <Alert
                      message="Your signature is required to complete the policy"
                      type="info"
                      showIcon
                      className={styles.signatureAlert}
                    />
                    <div className={styles.signatureUploadArea}>
                      {fileList && (fileList?.url || signaturePreviewUrl) ? (
                        <>
                          <img
                            src={signaturePreviewUrl || fileList.url}
                            alt="Signature Preview"
                            className={styles.signatureImage}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setFileList();
                              setSignaturePreviewUrl(null);
                              form.resetFields();
                            }}
                            className={styles.removeBtn}
                          >
                            <Trash2 size={14} />
                            Remove
                          </button>
                        </>
                      ) : fileList && getDocumentFileId(fileList) ? (
                        <div className={styles.loadingState}>
                          <Loader size={16} className={styles.spinner} /> Loading signature...
                        </div>
                      ) : (
                        <>
                          <Upload
                            showUploadList={false}
                            beforeUpload={() => false}
                            onChange={handleChange}
                            accept="image/png, image/jpeg"
                            fileList={fileList ? [fileList] : []}
                          >
                            <Button
                              type="primary"
                              className={styles.uploadBtn}
                              loading={fileUploading}
                            >
                              <UploadIcon size={16} />
                              {fileUploading ? "Uploading..." : "Upload Signature"}
                            </Button>
                          </Upload>
                          <div className={styles.uploadHint}>
                            Upload a clear image of your signature (JPG, PNG)
                          </div>
                        </>
                      )}
                    </div>
                    <div className={styles.signatureTips}>
                      <ul>
                        {signatureTooltip.map((tip, idx) => (
                          <li key={idx}>{tip}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            } else if (field?.type === "date") {
              return (
                <Form.Item key={index} label={field?.label} name={field?.field_name}>
                  <DatePicker
                    className={styles.fieldDatePicker}
                    format={"YYYY-MM-DD"}
                    placeholder=""
                    disabled
                  />
                </Form.Item>
              );
            }
          })}
      </Form>

      <div className={styles.actions}>
        <Button
          className={styles.backBtn}
          onClick={() => setCurrent((prev) => prev - 1)}
        >
          <ArrowLeft size={16} />
          Back
        </Button>
        <Button
          type="primary"
          className={styles.continueBtn}
          onClick={nextStep}
        >
          Continue to Payment
          <ArrowRight size={16} />
        </Button>
      </div>
    </div>
  );
}

export default Index;
